/**
 * @module features/profile/api/profile-api
 * @description TanStack Query hooks and API calls for Super Admin profile and security settings.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient, ApiSuccessEnvelope } from "@/lib/api-client";
import {
  ProfileDto,
  TotpSetupResponse,
  TotpConfirmResponse,
} from "@/types/profile";

export async function getProfile(): Promise<ProfileDto> {
  const response = await apiClient.get<ApiSuccessEnvelope<ProfileDto>>(
    "/super-admin/profile/"
  );
  return response.data.data;
}

export async function updateProfile(data: {
  name?: string;
  contact_number?: string;
  country_code?: string;
}): Promise<ProfileDto> {
  const response = await apiClient.patch<ApiSuccessEnvelope<ProfileDto>>(
    "/super-admin/profile/",
    data
  );
  return response.data.data;
}

export async function changePassword(data: {
  current_password: string;
  new_password: string;
}): Promise<void> {
  await apiClient.post("/super-admin/profile/password/", data);
}

export async function getTotpSetup(): Promise<TotpSetupResponse> {
  const response = await apiClient.post<ApiSuccessEnvelope<TotpSetupResponse>>(
    "/super-admin/profile/2fa/setup/"
  );
  return response.data.data;
}

export async function confirmTotp(data: {
  secret: string;
  code: string;
}): Promise<TotpConfirmResponse> {
  const response = await apiClient.post<ApiSuccessEnvelope<TotpConfirmResponse>>(
    "/super-admin/profile/2fa/confirm/",
    data
  );
  return response.data.data;
}

export async function disableTotp(password: string): Promise<void> {
  await apiClient.post("/super-admin/profile/2fa/disable/", { password });
}

export async function getPasskeyRegisterOptions(): Promise<any> {
  const response = await apiClient.post<ApiSuccessEnvelope<any>>(
    "/super-admin/profile/passkey/register/options/"
  );
  return response.data.data;
}

export async function verifyPasskeyRegister(data: {
  credential: any;
  device_label: string;
}): Promise<any> {
  const response = await apiClient.post<ApiSuccessEnvelope<any>>(
    "/super-admin/profile/passkey/register/verify/",
    data
  );
  return response.data.data;
}

export async function deletePasskey(passkeyId: string): Promise<void> {
  await apiClient.delete(`/super-admin/profile/passkey/${passkeyId}/`);
}

// -------------------------------------------------------------
// Hooks
// -------------------------------------------------------------

export function useProfile() {
  return useQuery({
    queryKey: ["profile"],
    queryFn: getProfile,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: changePassword,
  });
}

export function useTotpSetup() {
  return useMutation({
    mutationFn: getTotpSetup,
  });
}

export function useConfirmTotp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: confirmTotp,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
}

export function useDisableTotp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: disableTotp,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
}

export function usePasskeyRegisterOptions() {
  return useMutation({
    mutationFn: getPasskeyRegisterOptions,
  });
}

export function useVerifyPasskeyRegister() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: verifyPasskeyRegister,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
}

export function useDeletePasskey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePasskey,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
}
